/**
 * spineProgress — the Spine header's "Drafting · N sections" line and its
 * progress bar, derived from the spine rows alone.
 *
 * Pure: no React, no I/O. Counts only what the rows already say; nothing is
 * estimated.
 */

import type { SpineSection } from "../hooks/useVariantCRun";
import type { AgentActivityLine, DraftStage } from "./agentActivityFeed";

export type SpinePhase = "drafting" | "drafted" | "paused" | "failed";

export interface SpineProgress {
  phase: SpinePhase;
  /** Header text, e.g. "Drafting · 6 sections". */
  label: string;
  /** Sections the run drafts (skipped modules excluded). */
  total: number;
  /** Sections settled: drafted, flagged/failed or awaiting approval. */
  done: number;
  /** Sections whose drafting failed (server ``module_error``). */
  failed: number;
  /** 0..100 for the bar. */
  percent: number;
  /** Compact per-state tally for the header, e.g. "1 done · 1 drafting ·
   *  5 queued". Zero counts drop out; failures are left to the header's own
   *  red "N failed" meta. Empty string when there is nothing to count. */
  summary: string;
}

const PHASE_WORD: Record<SpinePhase, string> = {
  drafting: "Drafting",
  drafted: "Drafted",
  // Same words the shell / status strip already use for these states.
  paused: "Paused",
  failed: "Run failed",
};

export function deriveSpineProgress(
  sections: readonly SpineSection[],
  opts: { paused?: boolean; runFailed?: boolean } = {},
): SpineProgress {
  const counted = sections.filter((s) => s.status !== "skipped");
  const total = counted.length;
  const done = counted.filter((s) => s.status !== "queued" && s.status !== "drafting").length;
  const failed = counted.filter((s) => s.failed).length;
  const anyActive = counted.some((s) => s.status === "drafting" || s.auditPhase === "running");
  const complete = total > 0 && done === total && !anyActive;

  let phase: SpinePhase;
  if (opts.runFailed) phase = "failed";
  else if (complete) phase = "drafted";
  else if (opts.paused) phase = "paused";
  else phase = "drafting";

  const percent = total === 0 ? 0 : complete ? 100 : (done / total) * 100;
  const count = (pred: (s: SpineSection) => boolean) => counted.filter(pred).length;
  const auditing = (s: SpineSection) => s.auditPhase === "running";
  const tally: Array<[number, string]> = [
    [count((s) => s.status === "drafted" && !auditing(s)), "done"],
    [count((s) => s.status === "drafting"), "drafting"],
    [count((s) => s.status !== "drafting" && auditing(s)), "auditing"],
    [count((s) => s.status === "needs_approval"), "to approve"],
    [count((s) => s.status === "flagged" && !s.failed), "flagged"],
    [count((s) => s.status === "queued"), "queued"],
  ];
  const summary = tally
    .filter(([n]) => n > 0)
    .map(([n, word]) => `${n} ${word}`)
    .join(" · ");
  return {
    phase,
    label: `${PHASE_WORD[phase]} · ${total} section${total === 1 ? "" : "s"}`,
    total,
    done,
    failed,
    percent,
    summary,
  };
}

/** Sections that must settle, since the baseline, before a time-left
 *  estimate is shown: one finished section says little about the pace. */
export const TIME_LEFT_MIN_SETTLED = 2;

/** "About N min left" for a drafting run, from the pace sections have settled
 *  at since ``base`` (when this pane first saw the run drafting, and how many
 *  had settled then). Null until the pace is known, or when nothing is left.
 *  Modules draft in parallel, so the pace is the run's, not one section's. */
export function estimateTimeLeft(
  base: { at: number; done: number },
  now: number,
  done: number,
  total: number,
): string | null {
  const settled = done - base.done;
  const left = total - done;
  if (settled < TIME_LEFT_MIN_SETTLED || left <= 0 || now <= base.at) return null;
  const minutes = Math.ceil(((now - base.at) / settled) * left / 60000);
  if (minutes <= 1) return "about a minute left";
  if (minutes < 90) return `about ${minutes} min left`;
  const hours = Math.round(minutes / 30) / 2;
  return `about ${hours} hours left`;
}

// ---------------------------------------------------------------------------
// Per-section stage — the "Now drafting" card's stepper
// ---------------------------------------------------------------------------

export type DraftStep = DraftStage | "done";

export const DRAFT_STEPS: ReadonlyArray<{ id: DraftStep; label: string }> = [
  { id: "sources", label: "Sources" },
  { id: "drafting", label: "Drafting" },
  { id: "reviewing", label: "Review" },
  { id: "done", label: "Done" },
];

const STEP_RANK: Record<DraftStep, number> = { sources: 0, drafting: 1, reviewing: 2, done: 3 };

/** The later of two steps (null-safe). */
export function laterStep(a: DraftStep | null, b: DraftStep | null): DraftStep | null {
  if (!a) return b;
  if (!b) return a;
  return STEP_RANK[b] > STEP_RANK[a] ? b : a;
}

/**
 * Stage evidence in the row's live subline (the SSE adapter's retrieval /
 * draft_starting / text_chunk wording). Strips the "[n/m] " progress prefix
 * and an optional "Agent · " label before matching.
 */
export function sublineStage(subline: string | undefined): DraftStage | null {
  if (!subline) return null;
  const s = subline.replace(/^\[\d+\/\d+\]\s*/, "");
  if (/(^|· )drafting\b/i.test(s)) return "drafting";
  if (/(^|· )(planning|retrieving|follow-up)\b/i.test(s)) return "sources";
  if (/^checks\b/i.test(s)) return "reviewing";
  return null;
}

/**
 * The subline without its leading stage word, for rows whose chip already
 * names the stage ("retrieving · 3/8 · bioreactor" -> "3/8 · bioreactor").
 * Keeps any "[n/m]" pass prefix and every other detail; "" when the stage
 * word was all there was.
 */
export function sublineDetail(subline: string): string {
  const m = subline.match(/^(\[\d+\/\d+\]\s*)?([\s\S]*)$/);
  const prefix = m?.[1] ?? "";
  const rest = (m?.[2] ?? subline)
    // Agent tag ("Drafter · locating …"): the row already says who is working.
    .replace(/^(drafter|planner|lead planner|reviewer|retriever|auditor|superintendent)\s*·\s*/i, "")
    .replace(/^(drafting|planning|retrieving|checks)\b\s*(·\s*)?/i, "")
    .trim();
  return rest ? `${prefix}${rest}` : prefix.trim();
}

/**
 * Where a running section stands, from what the client really holds: its
 * status / audit phase, the subline wording and the stage tags on its live
 * feed. ``prior`` is the step already reached on this pass — the result never
 * moves backwards (a drafter re-reading a source mid-draft is still drafting).
 * Null when nothing says yet; the caller then draws no stepper.
 */
export function deriveDraftStep(
  section: SpineSection,
  lines: readonly AgentActivityLine[] | undefined,
  prior: DraftStep | null = null,
): DraftStep | null {
  if (section.auditPhase === "running") return laterStep(prior, "reviewing");
  if (section.status === "drafted") return "done";
  if (section.status !== "drafting") return null;
  let step = laterStep(prior, sublineStage(section.subline));
  for (const line of lines ?? []) step = laterStep(step, line.stage ?? null);
  return step;
}
